import React, { useEffect, useState, useCallback, useRef } from 'react';
import MaterialTable from 'material-table';
import { Select, MenuItem, Button, Grid, Typography } from '@material-ui/core';
import { CloudUpload, CloudDownload, Delete } from '@material-ui/icons';
import HeaderAndLayout from '../layouts/HeaderAndLayout';
import FormDialog from './dialog';
import BulkUploadDialog from './BulkUploadDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import ExportFormatDialog from '../common/ExportFormatDialog';
import { toggleTableRowSelection } from '../common/toggleTableRowSelection';
import './resources.css';
import axios from "axios";
import BeatLoader from 'react-spinners/BeatLoader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Resources() {
    const tableRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [rowUpdate, setRowUpdate] = useState(false)
    const [formValues, setFormValues] = useState({});
    const [tableData, setTableData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [callingBackend, setCallingBackend] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState(false);
    const [failMessage, setFailMessage] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        message: "",
        ids: [],
    });

    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
    };

    const notify = useCallback(() => successMessage ? toast.success('Action successful!', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    }) : failMessage ? toast.error('Action failed!', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    }) : null, [successMessage, failMessage]);

    const loadData = async () => {
        try {
            setCallingBackend(true);
            const { data } = await axios.get("/resource/admin", axiosConfig);
            setTableData(data);
            setSelectedRows([]);
            setCallingBackend(false);
        } catch (error) {
            setFailMessage(true);
            setCallingBackend(false);
        }
    };

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps  
    }, []); // Load data only once when component mounts

    useEffect(() => {
        if (successMessage || failMessage) {
            notify();
            setSuccessMessage(false);
            setFailMessage(false);
        }
    }, [successMessage, failMessage, notify]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setRowUpdate(false);
        setFormValues({});
        setOpen(false);
    };

    const onChange = (e) => {
        setFormValues(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value
        }));
    }

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();

            reader.onloadend = () => {
                setFormValues(prevState => ({
                    ...prevState,
                    image: reader.result
                }));
            };

            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleFormSubmit = async (e) => {
        try {
            if (!rowUpdate) {
                const ownerValue = formValues.owner || localStorage.getItem('id');
                const submitData = {
                    ...formValues,
                    owner: ownerValue,
                    ownerApproval: true
                };
                await axios.post("/resource", submitData, axiosConfig);
            } else {
                await axios.put(`/resource/${formValues._id}`, formValues, axiosConfig);
            }
            
            setRowUpdate(false);
            setOpen(false);
            setFormValues({});
            setSuccessMessage(true);
            loadData();
            e.preventDefault();
        } catch (error) {
            console.error('Form submission error:', error);
            setFailMessage(true);
        }
    };

    const handleBulkUploadSuccess = (result) => {
        setSuccessMessage(true);
        
        const hasInvalidUrls = result.data && result.data.invalidUrls && result.data.invalidUrls.length > 0;
        const hasValidationErrors = result.data && result.data.summary && result.data.summary.validationErrors > 0;
        
        if (hasInvalidUrls || hasValidationErrors) {
            const totalIssues = result.data.summary?.validationErrors || result.data.invalidUrls?.length || 0;
            toast.warning(`Upload completed with ${totalIssues} issue(s) that need attention. Please review and update these resources.`, {
                position: "bottom-right",
                autoClose: 8000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
        
        if (!hasInvalidUrls && !hasValidationErrors) {
            setBulkUploadOpen(false);
        }
        
        loadData();
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleExport = async (format = "xlsx") => {
        try {
            setExporting(true);
            const response = await axios.get(`/resource/admin/export?format=${format}`, {
                ...axiosConfig,
                responseType: "blob",
            });
            const filename =
                format === "xlsx" ? "resources-export.xlsx" : "resources-export.csv";
            downloadBlob(response.data, filename);
            toast.success(`Exported ${tableData.length} resource(s). Sheet is ready for bulk import.`);
            setExportDialogOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to export resources");
        } finally {
            setExporting(false);
        }
    };

    const requestDeleteByIds = (ids, label) => {
        if (!ids.length) {
            toast.info("No resources selected");
            return;
        }
        setConfirmDialog({
            open: true,
            title: "Delete resources",
            message: `Delete ${ids.length} ${label}? This cannot be undone.`,
            ids,
        });
    };

    const handleConfirmDelete = async () => {
        const ids = confirmDialog.ids || [];
        if (!ids.length) {
            setConfirmDialog({ open: false, title: "", message: "", ids: [] });
            return;
        }
        try {
            setDeleting(true);
            const { data } = await axios.post(
                "/resource/admin/bulk-delete",
                { ids },
                axiosConfig
            );
            toast.success(
                `Deleted ${data?.summary?.deleted || 0} resource(s).`
            );
            setConfirmDialog({ open: false, title: "", message: "", ids: [] });
            loadData();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to delete resources");
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteSelected = () => {
        const ids = selectedRows.map((row) => row._id).filter(Boolean);
        requestDeleteByIds(ids, "selected resource(s)");
    };

    const setTableSelection = (checked) => {
        const table = tableRef.current;
        if (table?.dataManager) {
            table.dataManager.changeAllSelected(checked);
            table.setState(table.dataManager.getRenderState(), () => {
                const selected = (table.dataManager.data || []).filter(
                    (row) => row?.tableData?.checked
                );
                setSelectedRows(selected);
            });
            return;
        }
        setSelectedRows(checked ? tableData : []);
    };

    const handleSelectAll = () => {
        setTableSelection(true);
        toast.info(`Selected all ${tableData.length} resource(s)`);
    };

    const columns = [
        { title: "Code", field: "code" },
        { title: "Name", field: "name" },
        { title: "Type", field: "type", render: rowData => rowData.type ? rowData.type.code : '' },
        { title: "Category", field: "category", render: rowData => rowData.category ? rowData.category.code : '' },
        {
            title: "Owner", field: "owner", render: rowData => rowData.owner && rowData.ownerApproval ?
                rowData.owner.email : rowData.owner && !rowData.ownerApproval ? "Needs Claim Approval" : ''
        },
        {
            title: 'Status',
            field: 'status',
            editComponent: props => (
                <Select
                    value={props.value}
                    onChange={e => props.onChange(e.target.value)}
                >
                    <MenuItem value={'pending'}>Pending</MenuItem>
                    <MenuItem value={'approved'}>Approved</MenuItem>
                    <MenuItem value={'inactive'}>Inactive</MenuItem>
                </Select>
            )
        }
    ]

    return (
        <div className="container" style={{ marginTop: "2rem" }}>
            <HeaderAndLayout activeItem={"resources"} />
            <Grid container spacing={2} justify="flex-end" style={{ marginBottom: "1rem" }}>
                <Grid item>
                    <Button
                        style={{ backgroundColor: "#1565c0", color: "#fff" }}
                        variant="contained"
                        startIcon={<CloudDownload />}
                        disabled={exporting || !tableData.length}
                        onClick={() => setExportDialogOpen(true)}
                    >
                        {exporting ? "Exporting..." : "Export"}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        style={{ backgroundColor: "#009E60", color: "#ffff" }}
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setBulkUploadOpen(true)}
                    >
                        Bulk Upload
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        style={{ backgroundColor: "#009E60", color: "#ffff" }}
                        variant="contained"
                        onClick={handleClickOpen}
                    >
                        + Create Resource
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" style={{ marginBottom: "0.75rem" }}>
                <Grid item>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!tableData.length}
                        onClick={handleSelectAll}
                    >
                        Select All
                    </Button>
                </Grid>
                {selectedRows.length > 0 && (
                    <>
                        <Grid item>
                            <Typography variant="body2" color="textSecondary">
                                Selected: {selectedRows.length} / {tableData.length}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                size="small"
                                variant="contained"
                                style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                                startIcon={<Delete />}
                                disabled={deleting}
                                onClick={handleDeleteSelected}
                            >
                                Delete Selected
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>

            {callingBackend ? <BeatLoader /> :
                <MaterialTable
                    tableRef={tableRef}
                    columns={columns}
                    data={tableData}
                    title="Resources"
                    options={{ 
                        search: true, 
                        filtering: false, 
                        paging: true,
                        pageSize: 10,
                        pageSizeOptions: [10, 25, 50, 100],
                        paginationType: "stepped", 
                        actionsColumnIndex: -1, 
                        addRowPosition: "first",
                        selection: true,
                        showTextRowsSelected: true,
                        searchFieldAlignment: "left",
                        searchFieldVariant: "outlined",
                        searchFieldStyle: {
                            marginLeft: '8px'
                        },
                        filterCellStyle: {
                            padding: '8px'
                        },
                        rowStyle: { cursor: 'pointer' },
                    }}
                    onSelectionChange={(rows) => setSelectedRows(rows || [])}
                    onRowClick={(event, rowData) =>
                        toggleTableRowSelection(event, rowData, tableRef, setSelectedRows)
                    }
                    actions={[
                        {
                            icon: 'edit',
                            tooltip: 'Edit Resource',
                            onClick: (event, rowData) => {
                                const row = Array.isArray(rowData) ? rowData[0] : rowData;
                                if (!row) return;
                                setFormValues(row);
                                setRowUpdate(true);
                                setOpen(true);
                            }
                        }
                    ]}
                />}
            <FormDialog 
                open={open} 
                handleClose={handleClose} 
                data={formValues} 
                onChange={onChange} 
                handleImageChange={handleImageChange}
                handleFormSubmit={handleFormSubmit} 
                axiosConfig={axiosConfig} 
                rowUpdate={rowUpdate} 
            />
            <BulkUploadDialog 
                open={bulkUploadOpen} 
                onClose={() => setBulkUploadOpen(false)} 
                onUploadSuccess={handleBulkUploadSuccess}
                axiosConfig={axiosConfig}
            />
            <ExportFormatDialog
                open={exportDialogOpen}
                onClose={() => {
                    if (!exporting) setExportDialogOpen(false);
                }}
                onExport={handleExport}
                exporting={exporting}
                title="Export resources"
                description="Choose CSV or Excel for your resources export."
            />
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                confirmColor="danger"
                loading={deleting}
                onCancel={() => {
                    if (!deleting) {
                        setConfirmDialog({ open: false, title: "", message: "", ids: [] });
                    }
                }}
                onConfirm={handleConfirmDelete}
            />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    )
}

export default Resources
